import React, { useEffect } from "react";
import { Provider, useSelector, useDispatch } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { BrowserRouter as Router } from "react-router-dom";
import { Route, Redirect } from "react-router-dom";
import { getTags } from "actions/tags";
import { checkAppVersion } from "actions/user";
import Layout from "layout";
import { getAllProducts } from "actions/products";
import Loader from "components/Loader";
import Dashboard from "pages/dashboard";
import Catalog from "pages/catalog";
import BrowseCatalog from "pages/browse-catalog";
import EditProducts from "pages/edit-products";
import ProductDetail from "pages/product-detail";
import AccountSettings from "pages/account-settings";
import MyStores from "pages/my-stores/stores";
import MyStoresRegistration from "pages/my-stores/registration";
import WebPages from "pages/web-pages";
import ConnectToShopify from "pages/connect-to-shopify";
import Orders from "pages/orders";
import OrderDetails from "pages/order-details";
import Home from "pages/home";
import SyncProducts from "pages/my-stores/sync-products";
import AdminSettings from "pages/admin-settings";
import ConnectToWooCommerce from "pages/connect-to-woo-commerce";
import NewsRoom from "pages/news-room";
import AdminUsers from "pages/admin-users";
import CreateProduct from "pages/create-product";
import DtgPlus from "pages/dtg-plus";
import OrderStock from "pages/order-stock";
import SalesReport from "pages/sales-report";
import SyncProduct from "pages/sync-product";
import { ROUTES } from "shared/constants/routes";
import { store, persistor } from "store";
import Products from "pages/my-stores/products";
import Warehouse from "pages/warehouse";
import Inventory from "pages/inventory";
import FastPlatform from "pages/fast-platform";
import SubmittedStockOrders from "pages/submitted-stock-orders";
import PrintifyInvoices from "pages/printify-invoices";
import ZazzleInvoices from "pages/zazzle-invoices";
import PrintifyOrders from "pages/printify-orders";
import StaplesBulkOrders from "pages/staples-bulk-orders";
import PartnBrigham from "pages/partn-brigham-and-womens-hosp-program";
import StaplesArtFiles from "pages/staples-art-files";
import FulfillmentDays from "pages/fulfillment-days";
import SmartSinkPrint from "pages/smart-print-sink";
import AdminScreenPrinting from "pages/admin-screen-printing";
import BlinkSwag from "pages/blink-swag";
import Brikl from "pages/brikl";
import WamiSwag from "pages/wami-swag";
import PrintYourCause from "pages/print-your-cause";
import Orbitkit from "pages/orbitkit";
import Proforma from "pages/proforma";
import CottonCandy from "pages/cotton-candy";
import ImsRetail from "pages/ims-retail";
import AmericanDiversity from "pages/american-diversity";
import AmericanBusiness from "pages/american-business";
import Groups from "pages/groups";
import ErrorModal from "components/ErrorModal";
import IPU from "pages/ipu";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const App = () => {
  const dispatch = useDispatch();
  const loaderVisibility = useSelector((state) => state?.loader?.visibility);
  const auth = useSelector((state) => state?.auth);
  const products = useSelector((state) => state?.products?.data?.length);
  const tags = useSelector((state) => !!state?.tags?.data?.length);

  useEffect(() => {
    const loadData = () => {
      if (!products) {
        return dispatch(getAllProducts(auth));
      }

      if (!tags) {
        return dispatch(getTags());
      }
    };
    loadData();
  }, [dispatch, products, tags, auth]);

  useEffect(() => {
    const saveAppVersion = async () => {
      const version = await dispatch(checkAppVersion());
      window.localStorage.setItem("appVersion", version?.CurrentAppVersion);
    };
    saveAppVersion();
  }, [dispatch]);

  return (
    <React.Fragment>
      <Router>
        <Route exact path={ROUTES.NEWS_ROOM.LINK} component={NewsRoom} />
        <Route exact path={ROUTES.HOME.LINK} component={Home} />
        <Layout path={ROUTES.DASHBOARD.LINK} component={Dashboard} />
        <Layout path={ROUTES.CATALOG.LINK} component={Catalog} />
        <Layout path={ROUTES.BROWSE_CATALOG.LINK} component={BrowseCatalog} />
        <Layout
          path={`${ROUTES.PRODUCT_DETAIL.LINK}/:id`}
          component={ProductDetail}
        />
        <Layout
          path={ROUTES.ACCOUNT_SETTINGS.LINK}
          component={AccountSettings}
        />
        <Layout path={ROUTES.WEB_PAGES.LINK} component={WebPages} />
        <Layout
          path={ROUTES.PRINT_YOUR_CAUSE.LINK}
          component={PrintYourCause}
        />

        <Layout path={ROUTES.ORBIT_KIT.LINK} component={Orbitkit} />
        <Layout path={ROUTES.PROFORMA.LINK} component={Proforma} />
        <Layout path={ROUTES.COTTON_CANDY.LINK} component={CottonCandy} />
        {/* <Layout path={ROUTES.FAST_PLATFORM.LINK} component={FastPlatform} /> */}

        <Layout path={ROUTES.IMS_RETAIL.LINK} component={ImsRetail} />

        {/* <Layout
          path={ROUTES.AMERICAN_DIVERSITY.LINK}
          component={AmericanDiversity}
        /> */}

        <Layout
          path={ROUTES.AMERICAN_BUSINESS.LINK}
          component={AmericanBusiness}
        />

        <Layout
          path={ROUTES.ADMIN_SCREEN_PRINTING.LINK}
          component={AdminScreenPrinting}
        />

        {/* <Layout
          path={ROUTES.SMART_PRINT_SINK.LINK}
          component={SmartSinkPrint}
        /> */}

        <Layout path={ROUTES.WAMI_SWAG.LINK} component={WamiSwag} />

        <Layout path={ROUTES.BLINK_SWAG.LINK} component={BlinkSwag} />

        <Layout path={ROUTES.BRIKL.LINK} component={Brikl} />

        <Layout path={ROUTES.MY_STORES.LINK} component={MyStores} />
        <Layout path={ROUTES.MY_STORES_PRODUCTS.LINK} component={Products} />

        <Layout
          path={ROUTES.MY_STORES_REGISTRATION.LINK}
          component={MyStoresRegistration}
        />
        <Layout
          path={ROUTES.CONNECT_TO_SHOPIFY.LINK}
          component={ConnectToShopify}
        />
        <Layout path={ROUTES.ORDERS.LINK} component={Orders} />
        <Layout
          path={`${ROUTES.ORDER_DETAILS.LINK}/:orderId`}
          component={OrderDetails}
        />
        <Layout
          path={`${ROUTES.ORDER_DETAILS.LINK}/:orderId/:shopName`}
          component={OrderDetails}
        />
        <Layout
          exact
          path={`${ROUTES.MY_STORES_SYNC_PRODUCT.LINK}/:productId/:marketPlaceType/:shopName`}
          component={SyncProduct}
        />
        <Layout path={ROUTES.ADMIN_SETTINGS.LINK} component={AdminSettings} />

        <Layout path={ROUTES.I_PROMOTE_U.LINK} component={IPU} />

        <Layout path={ROUTES.ADMIN_USERS.LINK} component={AdminUsers} />
        <Layout
          path={ROUTES.ADMIN_EDIT_PRODUCT.LINK}
          component={EditProducts}
        />
        <Layout
          path={ROUTES.MY_STORES_SYNC_PRODUCTS.LINK}
          component={SyncProducts}
        />
        <Layout
          path={ROUTES.ADMIN_CREATE_PRODUCT.LINK}
          component={CreateProduct}
        />
        <Layout
          path={ROUTES.CONNECT_TO_WOO_COMMERCE.LINK}
          component={ConnectToWooCommerce}
        />
        <Layout path={ROUTES.ADMIN_SALES_REPORT.LINK} component={SalesReport} />
        <Layout path={ROUTES.USER_SALES_REPORT.LINK} component={SalesReport} />
        <Layout path={ROUTES.WAREHOUSE.LINK} component={Warehouse} />
        <Layout path={ROUTES.ADMIN_DTG_PLUS.LINK} component={DtgPlus} />
        <Layout path={ROUTES.GROUPS.LINK} component={Groups} />
        <Layout path={ROUTES.INVENTORY.LINK} component={Inventory} />
        <Layout path={ROUTES.ORDER_STOCK.LINK} component={OrderStock} />
        <Layout
          path={ROUTES.SUBMITTED_ORDER_STOCK.LINK}
          component={SubmittedStockOrders}
        />
        {/* <Layout
          path={ROUTES.PRINTIFY_INVOICES.LINK}
          component={PrintifyInvoices}
        /> */}

        <Layout path={ROUTES.ZAZZLE_INVOICES.LINK} component={ZazzleInvoices} />
        {/* <Layout path={ROUTES.PRINTIFY_ORDERS.LINK} component={PrintifyOrders} /> */}
        <Layout path={ROUTES.PARTN_BRIGHAM.LINK} component={PartnBrigham} />
        <Layout
          path={ROUTES.STAPLES_BULK_ORDERS.LINK}
          component={StaplesBulkOrders}
        />
        <Layout
          path={ROUTES.STAPLES_ART_FILES.LINK}
          component={StaplesArtFiles}
        />
        <Layout
          path={ROUTES.FULFILLMENT_DAYS.LINK}
          component={FulfillmentDays}
        />
        <Route exact path={ROUTES.HOME.LINK}>
          {auth?.authenticated ? (
            <Redirect to={ROUTES.DASHBOARD.LINK} />
          ) : (
            <Redirect to={ROUTES.HOME.LINK} />
          )}
        </Route>
      </Router>
      {loaderVisibility && <Loader open={loaderVisibility} />}
    </React.Fragment>
  );
};

const Wrapper = () => {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <App />
        <ErrorModal />
      </PersistGate>
    </Provider>
  );
};

export default Wrapper;
